// ===========================================
// Fun Mode Header Components
// ===========================================
// Shared components for student fun mode layout
// Piano header, decorations, and user menu

import { Box, Typography, IconButton, Menu, MenuItem, Divider, ListItemIcon } from '@mui/material';
import { Logout as LogoutIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { StudentAvatar } from './StudentAvatar';

// Fun greeting messages
const FUN_GREETINGS = [
  "Let's make some music!",
  "Ready to rock?",
  "Time to play!",
  "Music time!",
];

// ===========================================
// FUN MODE DECORATIONS
// ===========================================

interface FunDecorationsProps {
  characterName: string;
}

export function FunDecorations({ characterName }: FunDecorationsProps) {
  const character = characterName.toLowerCase();

  return (
    <>
      {/* Character on left */}
      <Box
        component="img"
        src={`/avatars/${character}/${character}-happy.svg`}
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -40, md: 0, lg: 30 },
          bottom: { xs: '5%', md: '15%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.35,
          pointerEvents: 'none',
          transform: 'rotate(-5deg)',
          display: { xs: 'none', sm: 'block' },
          zIndex: 0,
        }}
      />

      {/* Character on right (different pose) */}
      <Box
        component="img"
        src={`/avatars/${character}/${character}-standing.svg`}
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -40, md: 0, lg: 30 },
          bottom: { xs: '25%', md: '35%' },
          width: { xs: 80, md: 120, lg: 160 },
          opacity: 0.35,
          pointerEvents: 'none',
          transform: 'rotate(5deg)',
          display: { xs: 'none', md: 'block' },
          zIndex: 0,
        }}
      />

      {/* Music notes - left side */}
      <Box
        component="img"
        src="/music-notes.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 50 },
          top: '35%',
          width: { xs: 50, md: 70 },
          opacity: 0.25,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
          zIndex: 0,
        }}
      />

      {/* Music notes - right side */}
      <Box
        component="img"
        src="/music-notes-3.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 50 },
          top: '75%',
          width: { xs: 50, md: 70 },
          opacity: 0.25,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
          zIndex: 0,
        }}
      />
    </>
  );
}

// ===========================================
// FUN MODE HEADER (Piano)
// ===========================================

interface FunHeaderProps {
  firstName: string;
  characterColor: string;
  userMenuButton?: React.ReactNode;
}

export function FunHeader({ firstName, characterColor, userMenuButton }: FunHeaderProps) {
  const greeting = FUN_GREETINGS[Math.floor(Math.random() * FUN_GREETINGS.length)];

  return (
    <>
      {/* Mobile Header - Logo centered, no piano */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          pb: 1,
          position: 'relative',
        }}
      >
        {/* User menu button - top right */}
        {userMenuButton && (
          <Box sx={{ position: 'absolute', top: 8, right: 0 }}>
            {userMenuButton}
          </Box>
        )}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 100,
            height: 'auto',
            mb: 1,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: characterColor,
            textAlign: 'center',
          }}
        >
          Hi {firstName}!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {greeting}
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mt: 3.5,
          mb: 2,
        }}
      >
        {/* User menu button - top right */}
        {userMenuButton && (
          <Box sx={{ position: 'absolute', top: { sm: -10, md: -6 }, right: { sm: 12, md: 12 }, zIndex: 10 }}>
            {userMenuButton}
          </Box>
        )}
        {/* Piano Background */}
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Logo - top left */}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 4, md: 8 },
            left: { sm: 12, md: 12 },
            maxWidth: { sm: 80, md: 100 },
            height: 'auto',
          }}
        />
        {/* Title overlay - bottom */}
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '1.75rem', md: '2.25rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          Hi {firstName}! {greeting}
        </Typography>
      </Box>
    </>
  );
}

// ===========================================
// FUN MODE USER MENU
// ===========================================

interface FunUserMenuProps {
  studentProfile: {
    avatar: string | null;
    birthDate: string;
    firstName: string;
    lastName: string;
  };
  characterColor: string;
  anchorEl: HTMLElement | null;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onLogout: () => void;
  onNavigate: (path: string) => void;
}

export function FunUserMenu({
  studentProfile,
  characterColor,
  anchorEl,
  onOpen,
  onClose,
  onLogout,
  onNavigate,
}: FunUserMenuProps) {
  return (
    <>
      <IconButton
        onClick={onOpen}
        sx={{
          bgcolor: 'rgba(255,255,255,0.9)',
          border: `2px solid ${characterColor}`,
          '&:hover': { bgcolor: 'white' },
        }}
      >
        <StudentAvatar
          avatar={studentProfile.avatar}
          birthDate={studentProfile.birthDate}
          firstName={studentProfile.firstName}
          lastName={studentProfile.lastName}
          size={32}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {studentProfile.firstName} {studentProfile.lastName}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { onClose(); onNavigate('account'); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Account Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}
